/**
 * FamilyMemberManagement Component
 *
 * Unified component for managing family organization contexts and members.
 * Shows a clean selector for multiple family orgs with inline member management.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Users,
  UserPlus,
  Mail,
  Clock,
  Trash2,
  Edit2,
  RefreshCw,
  Shield,
  AlertTriangle,
  Loader2,
  X,
  Check,
  GraduationCap,
  Home,
  Pencil
} from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { useOrganizationStore } from '../../stores/organizationStore'
import { isValidEmail } from '@shared/validation/email.js'
import { createLogger } from '../../utils/logger'

const logger = createLogger('FamilyMemberManagement')

const FamilyMemberManagement = () => {
  const { user, checkAuth } = useAuthStore()
  const { activeOrganization, setActiveOrganization, clearActiveOrganization } =
    useOrganizationStore()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [inviteEmail, setInviteEmail] = useState('')
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [studentInviteEmail, setStudentInviteEmail] = useState('')
  const [showStudentInviteForm, setShowStudentInviteForm] = useState(false)
  const [invitingStudent, setInvitingStudent] = useState(false)
  const [removingMember, setRemovingMember] = useState(null)
  const [confirmRemoval, setConfirmRemoval] = useState(null)
  const [editingInvitation, setEditingInvitation] = useState(null)
  const [editEmail, setEditEmail] = useState('')
  const [resendingInvitation, setResendingInvitation] = useState(null)
  const [renamingOrgId, setRenamingOrgId] = useState(null) // org ID being renamed, or null
  const [newOrgName, setNewOrgName] = useState('')
  const [savingRename, setSavingRename] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const autoInviteHandled = useRef(false)

  // Get family organizations from user
  const familyOrgs = user?.organizations?.filter((org) => org.type === 'family') || []
  const hasFamilyOrgs = familyOrgs.length > 0

  // Validate activeOrganization is in user's known orgs (prevents stale localStorage data)
  const isActiveOrgValid =
    activeOrganization?.type === 'family' &&
    familyOrgs.some((org) => org.id === activeOrganization.id)

  // Currently selected family org (for viewing members)
  const selectedFamilyOrg = isActiveOrgValid
    ? activeOrganization
    : familyOrgs.length === 1
      ? familyOrgs[0]
      : familyOrgs.length > 1
        ? familyOrgs[0] // Default to first if multiple and no valid active
        : null

  const fetchFamilyMembers = useCallback(async () => {
    if (!selectedFamilyOrg) {
      setData(null)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const response = await fetch(`/api/family/members?organizationId=${selectedFamilyOrg.id}`)

      if (!response.ok) {
        if (response.status === 403) {
          // User is no longer a member of this org - clear stale data.
          // sync:false — programmatic cleanup, not a deliberate switch.
          if (activeOrganization?.id === selectedFamilyOrg.id) {
            clearActiveOrganization({ sync: false })
          }
          setData(null)
          return
        }
        throw new Error('Failed to fetch family members')
      }

      const result = await response.json()
      if (result.success) {
        setData(result)
      }
    } catch (error) {
      logger.error('Error fetching family members:', error)
      toast.error('Failed to load family members')
    } finally {
      setLoading(false)
    }
  }, [selectedFamilyOrg, activeOrganization, clearActiveOrganization])

  useEffect(() => {
    fetchFamilyMembers()
  }, [fetchFamilyMembers])

  // Clear stale activeOrganization from localStorage if it's invalid
  useEffect(() => {
    if (activeOrganization?.type === 'family' && !isActiveOrgValid && familyOrgs.length > 0) {
      // activeOrganization is family type but not in user's orgs - clear it.
      // sync:false — stale-data correction, not a deliberate switch.
      clearActiveOrganization({ sync: false })
      toast('Family organization was updated. Please select one.', { icon: 'ℹ️' })
    }
  }, [activeOrganization, isActiveOrgValid, familyOrgs.length, clearActiveOrganization])

  // Auto-land on the user's only family org ONLY when they have nothing else to
  // default to: no active selection and no personal workspace. Without these
  // guards this fired on every render for any non-family active org, instantly
  // reverting a deliberate switch to Personal Account / a coaching workspace
  // (so the org switcher appeared to do nothing).
  const hasPersonalOrg = user?.organizations?.some((o) => o.type === 'personal')
  useEffect(() => {
    if (familyOrgs.length === 1 && !activeOrganization && !hasPersonalOrg) {
      setActiveOrganization(familyOrgs[0])
    }
  }, [familyOrgs, activeOrganization, hasPersonalOrg, setActiveOrganization])

  // Deep-link intent: `?action=invite-guardian` (e.g. from the "Add guardian" coaching action)
  // auto-opens the guardian invite form once permissions have loaded, then consumes the param so a
  // refresh or tab switch doesn't re-trigger it.
  useEffect(() => {
    if (autoInviteHandled.current) return
    if (searchParams.get('action') !== 'invite-guardian') return
    if (!data?.permissions?.canInviteGuardian) return
    autoInviteHandled.current = true
    setShowInviteForm(true)
    const next = new URLSearchParams(searchParams)
    next.delete('action')
    setSearchParams(next, { replace: true })
  }, [searchParams, data, setSearchParams])

  const handleSelectOrg = (org) => {
    setActiveOrganization(org)
    toast.success(`Switched to ${org.name}`)
  }

  const handleInviteGuardian = async (e) => {
    e.preventDefault()

    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address')
      return
    }

    if (!isValidEmail(inviteEmail.trim())) {
      toast.error('Please enter a valid email address')
      return
    }

    try {
      setInviting(true)
      const response = await fetch('/api/family/invite-guardian', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: inviteEmail.trim(),
          organizationId: selectedFamilyOrg?.id
        })
      })

      const result = await response.json()

      if (response.ok && result.success) {
        toast.success(`Invitation sent to ${inviteEmail}`)
        setInviteEmail('')
        setShowInviteForm(false)
        fetchFamilyMembers()
      } else {
        throw new Error(result.message || 'Failed to send invitation')
      }
    } catch (error) {
      toast.error(error.message)
    } finally {
      setInviting(false)
    }
  }

  const handleInviteStudent = async (e) => {
    e.preventDefault()

    if (!studentInviteEmail.trim()) {
      toast.error('Please enter an email address')
      return
    }

    if (!isValidEmail(studentInviteEmail.trim())) {
      toast.error('Please enter a valid email address')
      return
    }

    try {
      setInvitingStudent(true)
      const response = await fetch('/api/family/invite-student', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: studentInviteEmail.trim(),
          organizationId: selectedFamilyOrg?.id
        })
      })

      const result = await response.json()

      if (response.ok && result.success) {
        toast.success(`Invitation sent to ${studentInviteEmail}`)
        setStudentInviteEmail('')
        setShowStudentInviteForm(false)
        fetchFamilyMembers()
      } else {
        throw new Error(result.message || 'Failed to send invitation')
      }
    } catch (error) {
      toast.error(error.message)
    } finally {
      setInvitingStudent(false)
    }
  }

  const handleCancelInvitation = async (invitationId) => {
    try {
      const response = await fetch(`/api/family/invitations/${invitationId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        toast.success('Invitation cancelled')
        fetchFamilyMembers()
      } else {
        const result = await response.json()
        throw new Error(result.message || 'Failed to cancel invitation')
      }
    } catch (error) {
      toast.error(error.message)
    }
  }

  const handleUpdateInvitation = async (invitationId) => {
    if (!editEmail.trim()) {
      toast.error('Please enter an email address')
      return
    }

    if (!isValidEmail(editEmail.trim())) {
      toast.error('Please enter a valid email address')
      return
    }

    try {
      const response = await fetch(`/api/family/invitations/${invitationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: editEmail.trim() })
      })

      const result = await response.json()

      if (response.ok && result.success) {
        // The server reports honestly whether the address actually changed ("updated and resent" vs
        // "resent"), so editing without changing the email no longer falsely claims an update.
        toast.success(result.message || `Invitation updated for ${editEmail}`)
        setEditingInvitation(null)
        setEditEmail('')
        fetchFamilyMembers()
      } else {
        throw new Error(result.message || 'Failed to update invitation')
      }
    } catch (error) {
      toast.error(error.message)
    }
  }

  // Resend a pending invitation to the same address (dedicated action, distinct from Edit). Re-sends
  // the email and rotates the token so a guardian/student whose first email never arrived gets another.
  const handleResendInvitation = async (invitationId) => {
    setResendingInvitation(invitationId)
    try {
      const response = await fetch(`/api/family/invitations/${invitationId}/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      const result = await response.json()
      if (response.ok && result.success) {
        toast.success(result.message || 'Invitation resent')
        fetchFamilyMembers()
      } else {
        throw new Error(result.message || 'Failed to resend invitation')
      }
    } catch (error) {
      toast.error(error.message)
    } finally {
      setResendingInvitation(null)
    }
  }

  const handleRemoveMember = async (memberId, confirmDeletion = false) => {
    try {
      setRemovingMember(memberId)
      const response = await fetch(`/api/family/members/${memberId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmDeletion })
      })

      const result = await response.json()

      if (response.ok && result.success) {
        toast.success(result.message)
        setConfirmRemoval(null)
        fetchFamilyMembers()
      } else if (result.requiresConfirmation) {
        setConfirmRemoval({
          memberId,
          memberName: result.memberName,
          memberAge: result.memberAge
        })
      } else {
        throw new Error(result.message || 'Failed to remove member')
      }
    } catch (error) {
      toast.error(error.message)
    } finally {
      setRemovingMember(null)
    }
  }

  const handleRenameOrg = async () => {
    if (!newOrgName.trim() || !renamingOrgId) {
      toast.error('Please enter a name')
      return
    }

    try {
      setSavingRename(true)
      const response = await fetch(`/api/family/rename?organizationId=${renamingOrgId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newOrgName.trim() })
      })

      const result = await response.json()

      if (response.ok && result.success) {
        toast.success('Organization renamed')
        setRenamingOrgId(null)
        setNewOrgName('')
        // Update activeOrganization if it's the one being renamed
        if (activeOrganization?.id === renamingOrgId) {
          setActiveOrganization({ ...activeOrganization, name: result.organization.name })
        }
        // Refresh user data to get updated org names
        await checkAuth()
        fetchFamilyMembers()
      } else {
        throw new Error(result.message || 'Failed to rename organization')
      }
    } catch (error) {
      toast.error(error.message)
    } finally {
      setSavingRename(false)
    }
  }

  // Don't render if user has no family orgs
  if (!hasFamilyOrgs) {
    return null
  }

  const { members, pendingInvitations, seats, permissions } = data || {}
  const guardianInvitations = pendingInvitations?.filter((inv) => inv.role === 'guardian') || []
  const studentInvitations = pendingInvitations?.filter((inv) => inv.role === 'student') || []

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white dark:bg-gray-800 rounded-xl shadow-sm flex items-center justify-center">
              <Home className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Family Management
              </h3>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {familyOrgs.length === 1 ? (
                  renamingOrgId ? (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault()
                        handleRenameOrg()
                      }}
                      className="flex items-center gap-2"
                    >
                      <input
                        type="text"
                        value={newOrgName}
                        onChange={(e) => setNewOrgName(e.target.value)}
                        className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500"
                        autoFocus
                        placeholder="Family name"
                      />
                      <button
                        type="submit"
                        disabled={savingRename}
                        className="p-1 text-green-600 hover:text-green-700"
                      >
                        {savingRename ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setRenamingOrgId(null)
                          setNewOrgName('')
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </form>
                  ) : (
                    <span className="flex items-center gap-2">
                      {selectedFamilyOrg?.name || familyOrgs[0].name}
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                        {familyOrgs[0].role}
                      </span>
                      {familyOrgs[0].isPrimaryGuardian && (
                        <button
                          onClick={() => {
                            const orgId = selectedFamilyOrg?.id || familyOrgs[0].id
                            setNewOrgName(selectedFamilyOrg?.name || familyOrgs[0].name)
                            setRenamingOrgId(orgId)
                          }}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 rounded hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
                        >
                          <Pencil className="w-3 h-3" />
                          Edit
                        </button>
                      )}
                    </span>
                  )
                ) : (
                  `${familyOrgs.length} family organizations`
                )}
              </div>
            </div>
          </div>
          {selectedFamilyOrg && (
            <button
              onClick={fetchFamilyMembers}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {/* Rename Form (for multi-org view) */}
      {familyOrgs.length > 1 && renamingOrgId && (
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/20">
          <p className="text-xs font-medium text-amber-700 dark:text-amber-400 uppercase tracking-wide mb-2">
            Rename Family
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleRenameOrg()
            }}
            className="flex items-center gap-2"
          >
            <input
              type="text"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
              autoFocus
              placeholder="Enter new name"
            />
            <button
              type="submit"
              disabled={savingRename}
              className="px-3 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {savingRename ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setRenamingOrgId(null)
                setNewOrgName('')
              }}
              className="px-3 py-2 text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* Family Org Selector (only if multiple orgs) */}
      {familyOrgs.length > 1 && (
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            Select Family
          </p>
          <div className="space-y-2">
            {familyOrgs.map((org) => {
              const isSelected = selectedFamilyOrg?.id === org.id
              return (
                <div
                  key={org.id}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                    isSelected
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                  }`}
                >
                  <button
                    onClick={() => handleSelectOrg(org)}
                    className="flex items-center gap-3 flex-1"
                  >
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                        isSelected
                          ? 'bg-primary-100 dark:bg-primary-800'
                          : 'bg-gray-100 dark:bg-gray-700'
                      }`}
                    >
                      <Home
                        className={`w-4 h-4 ${
                          isSelected
                            ? 'text-primary-600 dark:text-primary-400'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      />
                    </div>
                    <div className="text-left">
                      <p
                        className={`text-sm font-medium ${
                          isSelected
                            ? 'text-primary-900 dark:text-primary-100'
                            : 'text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        {org.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{org.role}</p>
                    </div>
                  </button>
                  <div className="flex items-center gap-2">
                    {org.isPrimaryGuardian && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setNewOrgName(org.name)
                          setRenamingOrgId(org.id)
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 dark:text-primary-400 bg-white dark:bg-gray-700 border border-primary-200 dark:border-primary-700 rounded hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors"
                      >
                        <Pencil className="w-3 h-3" />
                        Edit
                      </button>
                    )}
                    {isSelected && (
                      <Check className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Content */}
      {!selectedFamilyOrg ? (
        <div className="p-8 text-center">
          <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">Select a family to view members</p>
        </div>
      ) : loading ? (
        <div className="p-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
        </div>
      ) : !data ? (
        <div className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">Unable to load family members</p>
        </div>
      ) : (
        <div className="p-5">
          {/* Members Grid */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Guardians Column */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-md flex items-center justify-center">
                    <Shield className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Guardians
                  </h4>
                  <span className="text-xs text-gray-500 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                    {members?.guardians?.length || 0}/{seats?.guardians?.max || 2}
                  </span>
                </div>
                {permissions?.canInviteGuardian && !showInviteForm && (
                  <button
                    onClick={() => setShowInviteForm(true)}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    Invite
                  </button>
                )}
              </div>

              {/* Invite Form */}
              {showInviteForm && (
                <motion.form
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  onSubmit={handleInviteGuardian}
                  className="space-y-2"
                >
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="Guardian's email address"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={inviting}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {inviting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Mail className="w-4 h-4" />
                      )}
                      {inviting ? 'Sending...' : 'Send Invite'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowInviteForm(false)
                        setInviteEmail('')
                      }}
                      className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </motion.form>
              )}

              {/* Guardian List */}
              <div className="space-y-2">
                {members?.guardians?.length === 0 && guardianInvitations.length === 0 ? (
                  <EmptyState
                    icon={Shield}
                    message="No guardians"
                    action={permissions?.canInviteGuardian ? () => setShowInviteForm(true) : null}
                    actionLabel="Invite"
                  />
                ) : (
                  <>
                    {members?.guardians?.map((member) => (
                      <MemberCard
                        key={member.id}
                        member={member}
                        canRemove={permissions?.canRemoveMembers && member.userId !== user?.id}
                        onRemove={() =>
                          setConfirmRemoval({
                            memberId: member.userId,
                            memberName: member.user?.name || member.user?.email,
                            memberType: 'guardian'
                          })
                        }
                        isRemoving={removingMember === member.userId}
                        variant="guardian"
                      />
                    ))}
                    {guardianInvitations.map((invitation) => (
                      <PendingInvitationCard
                        key={invitation.id}
                        invitation={invitation}
                        onCancel={() => handleCancelInvitation(invitation.id)}
                        onEdit={() => {
                          setEditingInvitation(invitation.id)
                          setEditEmail(invitation.email)
                        }}
                        onResend={() => handleResendInvitation(invitation.id)}
                        isResending={resendingInvitation === invitation.id}
                        isEditing={editingInvitation === invitation.id}
                        editEmail={editEmail}
                        onEditEmailChange={setEditEmail}
                        onSaveEdit={() => handleUpdateInvitation(invitation.id)}
                        onCancelEdit={() => {
                          setEditingInvitation(null)
                          setEditEmail('')
                        }}
                      />
                    ))}
                  </>
                )}
              </div>
            </div>

            {/* Students Column */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-emerald-100 dark:bg-emerald-900/30 rounded-md flex items-center justify-center">
                    <GraduationCap className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Students
                  </h4>
                  <span className="text-xs text-gray-500 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                    {members?.students?.length || 0}/{seats?.children?.max || 2}
                  </span>
                </div>
                {permissions?.canInviteStudent && !showStudentInviteForm && (
                  <button
                    onClick={() => setShowStudentInviteForm(true)}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded transition-colors"
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    Invite
                  </button>
                )}
              </div>

              {/* Invite Student Form */}
              {showStudentInviteForm && (
                <motion.form
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  onSubmit={handleInviteStudent}
                  className="space-y-2"
                >
                  <input
                    type="email"
                    value={studentInviteEmail}
                    onChange={(e) => setStudentInviteEmail(e.target.value)}
                    placeholder="Student's email address"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-gray-100"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={invitingStudent}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                    >
                      {invitingStudent ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Mail className="w-4 h-4" />
                      )}
                      {invitingStudent ? 'Sending...' : 'Send Invite'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowStudentInviteForm(false)
                        setStudentInviteEmail('')
                      }}
                      className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </motion.form>
              )}

              {/* Student List */}
              <div className="space-y-2">
                {members?.students?.length === 0 && studentInvitations.length === 0 ? (
                  <EmptyState
                    icon={GraduationCap}
                    message={
                      permissions?.canInviteStudent ? 'Invite your first student' : 'No students'
                    }
                  />
                ) : (
                  <>
                    {members?.students?.map((member) => (
                      <MemberCard
                        key={member.id}
                        member={member}
                        canRemove={permissions?.canRemoveMembers && member.userId !== user?.id}
                        onRemove={() =>
                          setConfirmRemoval({
                            memberId: member.userId,
                            memberName: member.user?.name || member.user?.email,
                            memberAge: member.user?.age,
                            isUnder13: member.user?.age < 13,
                            memberType: 'student'
                          })
                        }
                        isRemoving={removingMember === member.userId}
                        showAge
                        variant="student"
                      />
                    ))}
                    {studentInvitations.map((invitation) => (
                      <PendingInvitationCard
                        key={invitation.id}
                        invitation={invitation}
                        onCancel={() => handleCancelInvitation(invitation.id)}
                        onResend={() => handleResendInvitation(invitation.id)}
                        isResending={resendingInvitation === invitation.id}
                      />
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Member Removal Confirmation Modal */}
      <AnimatePresence>
        {confirmRemoval && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setConfirmRemoval(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={`flex items-center gap-3 mb-4 ${
                  confirmRemoval.isUnder13
                    ? 'text-red-600'
                    : confirmRemoval.memberType === 'guardian'
                      ? 'text-blue-600'
                      : 'text-amber-600'
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    confirmRemoval.isUnder13
                      ? 'bg-red-100 dark:bg-red-900/30'
                      : confirmRemoval.memberType === 'guardian'
                        ? 'bg-blue-100 dark:bg-blue-900/30'
                        : 'bg-amber-100 dark:bg-amber-900/30'
                  }`}
                >
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-semibold">
                  {confirmRemoval.isUnder13
                    ? 'Confirm Account Deletion'
                    : confirmRemoval.memberType === 'guardian'
                      ? 'Remove Guardian'
                      : 'Remove Student'}
                </h3>
              </div>

              {confirmRemoval.isUnder13 ? (
                <>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    <strong>{confirmRemoval.memberName}</strong> is under 13 years old. Removing
                    them will <strong>permanently delete their account</strong> and all associated
                    data.
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    This action cannot be undone.
                  </p>
                </>
              ) : confirmRemoval.memberType === 'guardian' ? (
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Are you sure you want to remove <strong>{confirmRemoval.memberName}</strong> from
                  this family? They will no longer be able to manage family members or view student
                  progress.
                </p>
              ) : (
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Are you sure you want to remove <strong>{confirmRemoval.memberName}</strong> from
                  this family? They will lose access to family resources but their account will
                  remain active.
                </p>
              )}

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setConfirmRemoval(null)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    handleRemoveMember(confirmRemoval.memberId, confirmRemoval.isUnder13)
                  }
                  disabled={removingMember === confirmRemoval.memberId}
                  className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 ${
                    confirmRemoval.isUnder13
                      ? 'bg-red-600 hover:bg-red-700'
                      : confirmRemoval.memberType === 'guardian'
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-amber-600 hover:bg-amber-700'
                  }`}
                >
                  {removingMember === confirmRemoval.memberId
                    ? 'Removing...'
                    : confirmRemoval.isUnder13
                      ? 'Delete Account'
                      : 'Remove'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Empty State Component
const EmptyState = ({ icon: Icon, message, action, actionLabel }) => (
  <div className="text-center py-4 px-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
    <Icon className="w-6 h-6 text-gray-400 mx-auto mb-1" />
    <p className="text-xs text-gray-500 dark:text-gray-400">{message}</p>
    {action && (
      <button
        onClick={action}
        className="mt-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
      >
        {actionLabel}
      </button>
    )}
  </div>
)

// Member Card Component
const MemberCard = ({ member, canRemove, onRemove, isRemoving, showAge, variant = 'guardian' }) => {
  const variants = {
    guardian: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      avatar: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
    },
    student: {
      bg: 'bg-emerald-50 dark:bg-emerald-900/20',
      avatar: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
    }
  }

  const style = variants[variant]

  return (
    <div
      className={`flex items-center justify-between p-2.5 rounded-lg ${style.bg} transition-colors`}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${style.avatar}`}
        >
          <span className="text-xs font-semibold">
            {member.user.firstName?.[0]}
            {member.user.lastName?.[0]}
          </span>
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {member.user.firstName} {member.user.lastName}
            </p>
            {member.isPrimaryGuardian && (
              <span className="text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                Primary
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {member.user.email}
            {showAge && member.user.age && <span className="ml-1">· {member.user.age}y</span>}
          </p>
        </div>
      </div>

      {canRemove && (
        <button
          onClick={onRemove}
          disabled={isRemoving}
          className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-50 transition-colors flex-shrink-0"
          title="Remove member"
        >
          {isRemoving ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Trash2 className="w-3.5 h-3.5" />
          )}
        </button>
      )}
    </div>
  )
}

// Pending Invitation Card Component
const PendingInvitationCard = ({
  invitation,
  onCancel,
  onEdit,
  onResend,
  isResending = false,
  isEditing,
  editEmail,
  onEditEmailChange,
  onSaveEdit,
  onCancelEdit
}) => {
  const expiresIn = Math.ceil((new Date(invitation.expiresAt) - new Date()) / (1000 * 60 * 60))
  const isExpired = expiresIn <= 0

  if (isEditing) {
    return (
      <div className="p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
        <div className="flex items-center gap-2">
          <input
            type="email"
            value={editEmail}
            onChange={(e) => onEditEmailChange(e.target.value)}
            className="flex-1 px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
            autoFocus
          />
          <button
            onClick={onSaveEdit}
            className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
          >
            <Check className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onCancelEdit}
            className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    )
  }

  // Status chip: an expired invite reads red (needs a resend), a live one reads amber with time left.
  const chip = isExpired ? (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
      <Clock className="w-3 h-3" />
      Expired
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
      <Clock className="w-3 h-3" />
      Invited · {expiresIn}h left
    </span>
  )

  const borderCls = isExpired
    ? 'border-red-300 dark:border-red-700'
    : 'border-amber-300 dark:border-amber-700'

  return (
    <div
      className={`flex items-center justify-between p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-dashed ${borderCls}`}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/40 rounded-full flex items-center justify-center flex-shrink-0">
          <Mail className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {invitation.email}
          </p>
          <div className="mt-0.5">{chip}</div>
        </div>
      </div>

      <div className="flex items-center gap-0.5 flex-shrink-0">
        {onResend && invitation.canModify && (
          <button
            onClick={onResend}
            disabled={isResending}
            className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors disabled:opacity-60"
            title="Resend invitation"
          >
            {isResending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
          </button>
        )}
        {onEdit && invitation.canModify && (
          <button
            onClick={onEdit}
            className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
            title="Edit email"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
        )}
        {invitation.canModify && (
          <button
            onClick={onCancel}
            className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            title="Cancel invitation"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  )
}

export default FamilyMemberManagement
